import React, { useMemo } from 'react';
import Loader from '../common/Loader';
import DashboardTarget from './DashboardTarget';

interface TargetNode {
  _id: string;
  name: string;
  children?: TargetNode[];
}

interface FlatTarget {
  _id: string;
  name: string;
  level: number;
}

interface DashboardTargetsProps {
  targets: TargetNode[];
  selectedTargets: string[];
  onTargetToggle: (id: string) => void;
  isLoading?: boolean;
}

// Flatten a target and its descendants into a level-tagged list. Plain arrays
// now that the dashboard slice is de-Immutabled (was List/Map/.flatten()).
const getTargetChildren = (target: TargetNode, level = 0): FlatTarget[] => {
  const result = [{
    _id: target._id,
    name: target.name,
    level: level
  }];

  if (target && target.children && target.children.length > 0) {
    target.children.forEach(child => {
      result.push(...getTargetChildren(child, level + 1));
    });
  }
  return result;
};

const DashboardTargets = ({ targets, selectedTargets, onTargetToggle, isLoading }: DashboardTargetsProps) => {
  // Flatten once per targets change, not on every render — toggling a checkbox
  // re-renders this list, and re-walking the whole tree each time was wasted work.
  // NB: `targets.map(getTargetChildren)` would pass map's INDEX as the second
  // argument — which is getTargetChildren's `level` — indenting each root target
  // one step further than the last. Call it with one argument so level defaults
  // to 0 and only real nesting indents.
  const flattened = useMemo(() => targets.map(t => getTargetChildren(t)), [targets]);
  // Set lookup instead of Array.includes per row: O(1) rather than O(n).
  const selectedIds = useMemo(() => new Set(selectedTargets), [selectedTargets]);

  return (
    <div>
      {isLoading && <Loader />}
      <div style={{height:"0.4em"}}></div>
      <div></div> {
        flattened.map((group, i) => {
          return <div key={i}>
            {group.map((target, j) =>
              <DashboardTarget
                key={j}
                target={target}
                onTargetToggle={onTargetToggle}
                isSelected={selectedIds.has(target._id)} />
            )}
          </div>
        })
      }
    </div>
  );
};

export default React.memo(DashboardTargets);
